<template>
  <v-container 
    v-if="!$apollo.loading"
    fill-height
    fluid
    grid-list-xl
  >
    <v-layout
      v-if="!editMode"
      justify-center
      wrap
    >
      <v-flex
        md5
        xs12
        mb-0
        mt-0
        pb-0
      >
        <h2>{{ $t('modules.products') }}</h2>
      </v-flex>
      <v-flex
        md3
        xs12
        mb-0
        mt-0
        :class="!$vuetify.breakpoint.xs ? 'pt-5 text-right': 'pt-0'"
        pb-0
        text-right
      >
        <v-text-field
          v-model.trim="search"
          name="search"
          max-length="100"
          style="width:220px"
          prepend-inner-icon="mdi-text-box-search-outline"
          @change="reload()"
        >
          <template v-slot:append>
            <v-icon
              v-if="search"
              style="cursor:pointer"
              @click="search = null;reload()"
            >
              mdi-close
            </v-icon>
          </template>
        </v-text-field>
      </v-flex>
      <v-flex
        md4
        xs12
        mb-0
        :class="!$vuetify.breakpoint.xs ? 'pt-11 text-right': 'pb-5'"
        pb-0
      >
        <v-menu
          style="display:inline;"
          offset-y
          right
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="mr-2"
              style="margin-top: -0.35rem;"
              :disabled="$apollo.loading"
              outlined 
              small
              text
              v-on="on"
            >
              {{ selectedCategory ? selectedCategory.name : $t('modules.productAll') }}
            </v-btn>
          </template>
          <v-sheet
            v-if="moduleProductsCategories"
            dense
          >
            <v-list-item
              v-for="(item, i) in [...allCategories, ...moduleProductsCategories]"
              :key="i"
              @click="filterCategory(item)"
            >
              <v-list-item-title>{{ item.name }}</v-list-item-title>
            </v-list-item>
          </v-sheet>
        </v-menu>
        <v-btn
          style="display:inline;margin-top:-6px;"
          small
          class="primary"
          @click="editMode = true"
        >
          {{ $t('globals.createButton') }}
        </v-btn>
      </v-flex>
      <v-flex
        v-if="moduleProducts && moduleProducts.length > 0"
        md12
      >
        <v-card>
          <v-data-table
            class="text-no-wrap" 
            :headers="headers"
            :items="moduleProducts"
            :hide-default-footer="true"
            :mobile-breakpoint="900"
            :items-per-page="200"
          >
            <template
              slot="headerCell"
              slot-scope="{ header }"
            >
              <span
                v-text="header.text"
              />
            </template>
            <template v-slot:item.image="{ item }">
              <v-avatar
                :tile="true"
                :size="40"
              >
                <v-img
                  :src="(item.image || require('@/images/no-image.png'))"
                  :lazy-src="item.image"
                  :max-height="item.image ? '60' : '24'"
                  :style="(item.image && item.bg_color) && 'border: 1px solid ' + item.bg_color"
                  contain
                />
              </v-avatar>  
            </template>
            <template v-slot:item.id="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.id }}
                  </span>
                </template>
                {{ $t('modules.productIdTooltip') }}
              </v-tooltip>
            </template>
            <template v-slot:item.name="{ item }">
              {{ item.name }}
            </template>
            <template v-slot:item.price_onetime="{ item }">
              <v-tooltip
                color="surface"
                max-width="300"
                top
              >
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    v-html="computePrice(item)"
                  />
                </template>
                <v-layout wrap>
                  <v-flex
                    v-for="(priceType, i) in pricing.filter(item => (item != 'price_onetime'))"
                    :key="i"
                    pa-1
                    xs6
                  >
                    <v-card
                      :style="'border: 1px solid #dddfdd'"
                    >
                      <b>{{ item[priceType] }} {{ item.currency }}</b><br>
                      <small>{{ $t('modules.product_'+ priceType) }}</small>
                    </v-card>
                  </v-flex>
                </v-layout>
              </v-tooltip>
            </template>
            <template v-slot:item.status="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon
                    small
                    :color="!item.hidden ? 'success': 'error'"
                    v-on="on"
                  >
                    {{ item.status == 'active' ? 'mdi-check': 'mdi-close' }}
                  </v-icon>
                </template>
                {{ $t(item.status == 'active' ? 'modules.productStatusActive' : 'modules.productStatusInactive') }}
              </v-tooltip>
            </template>
            <template v-slot:item.created_at="{ item }">
              <small>{{ item.created_at | moment("L") }}</small>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-menu
                offset-y
                left
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    outlined 
                    small
                    text
                    v-on="on"
                  >
                    {{ $t('globals.manageButton') }}
                  </v-btn>
                </template>
                <v-sheet dense>
                  <v-list-item
                    @click="editProduct(item)"
                  >
                    <v-list-item-title>{{ $t('globals.editButton') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="showCheckoutLink(item)"
                  >
                    <v-list-item-title>{{ $t('globals.checkoutLink') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="confirmDeleteProduct(item)"
                  >
                    <v-list-item-title>{{ $t('globals.deleteButton') }}</v-list-item-title>
                  </v-list-item>
                </v-sheet>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
      <v-flex
        v-else
        class="not-found"
      >
        <v-card>
          <v-icon>mdi-ghost</v-icon>
          <p>{{ $t('modules.productNotFound') }}</p>
        </v-card>
      </v-flex>
    </v-layout>
    <edit-form
      v-if="editMode"
      :product="selectedProduct"
      :categories="moduleProductsCategories"
      :whitelabel-uuid="getWhiteLabel.uuid"
      :module-data="moduleData"
      :products="moduleProducts"
      @closeEditMode="closeEditMode"
    />
    <CheckoutLinkDialog
      v-if="checkoutLinkDialog"
      :product="selectedProduct"
      :domain="domain"
      :show-dialog="checkoutLinkDialog"
      @closeCheckoutLinkDialog="closeCheckoutLinkDialog"
    />

    <helper-confirm-dialog
      v-if="confirmDialog"
      :show-dialog="confirmDialog"
      :text="$t('modules.productReallyDelete', {name: selectedProduct.name})"
      @closeConfirmDialog="closeConfirmDialog"
    />
  </v-container>
</template>

<script>

import PRODUCTS_QUERY from "@/graphql/queries/modules/Products.gql";
import PRODUCTS_CATEGORIES_QUERY from "@/graphql/queries/modules/ProductsCategories.gql";
import EditForm from "@/views/Modules/Products/NewOrEdit.vue";
import { mapGetters } from 'vuex'
import PRODUCT_DELETE_MUTATION from "@/graphql/mutations/modules/ProductDelete.gql";
import CheckoutLinkDialog from "@/views/Modules/Products/partial/CheckoutLinkDialog.vue";

export default {
  name: 'ModuleProducts',
  components: {
    CheckoutLinkDialog,
    EditForm
  },
  data() {
    return {
      params: {
        whitelabel_uuid: null,
      },
      loading: false,
      editMode: false,
      selectedCategory: null,
      selectedProduct: null,
      confirmDialog: false,
      checkoutLinkDialog: false,
      search: "",
      pricing: [
        "price_onetime", 
        "price_monthly", 
        "price_quartally", 
        "price_semiannually", 
        "price_annually"
      ],
      allCategories: [{id: null, "name": this.$t("modules.productAll")}],
      headers: [
        {
          sortable: false,
          text: '',
          value: 'image'
        },
        {
          sortable: false,
          text: 'ID',
          value: 'id'
        },
        {
          sortable: false,
          text: this.$t('modules.productName'),
          value: 'name',
        },
        {
          sortable: false,
          text: this.$t('modules.productPrice'),
          value: 'price_onetime',
        },
        {
          sortable: false,
          text: this.$t('modules.productStatus'),
          value: 'status'
        },
        {
          sortable: false,
          text: this.$t('modules.productAddedAt'),
          value: 'created_at'
        },
        {
          sortable: false,
          text: '',
          value: 'actions',
        },
      ]
    }
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    moduleData() {
      if (this.getWhiteLabel && this.getWhiteLabel.modules.length > 0) {
        return this.getWhiteLabel.modules.find(item => item.module == 'product')
      }
      return null
    },
    domain() {
      if(this.getWhiteLabel) {
        return this.getWhiteLabel.domain || `${this.getWhiteLabel.subdomain}.${this.$ls.get("config").domain}`
      }
      return ''
    }
  },
  watch: {
    getWhiteLabel(val) {
      if (this.routeNotAllowed(val, 'product')) {
        this.$router.push('/dashboard')
      }
      this.params.whitelabel_uuid = val.uuid
      this.moduleProducts = []
      this.$apollo.queries.moduleProducts.refetch()
    }
  },
  mounted() {
    if (this.getWhiteLabel) {
      this.params.whitelabel_uuid = this.getWhiteLabel.uuid
    }
  },
  methods: {
    computePrice(item) {
      let price = "-"
      const priceType = this.pricing.find(type => (item[type] > 0))
      if (priceType) {
        price = '<b>' + item[priceType] + ' ' + item.currency + '</b>' + (priceType != 'price_onetime' ? '<br/><small>' + this.$t('modules.product_'+ priceType) + '</small>' : '')
      }
      return price
    },
    closeConfirmDialog(confirmed) {
      this.confirmDialog = false
      if (confirmed) {
        this.deleteProduct()
      }
      this.selectedProduct = null
    },
    showCheckoutLink(item) {
      this.checkoutLinkDialog = true
      this.selectedProduct = item
    },
    confirmDeleteProduct(item) {
      this.confirmDialog = true
      this.selectedProduct = item
    },
    filterCategory(category) {
      this.selectedCategory = category.id ? category : null
      this.reload()
    },
    editProduct(item) {
      this.selectedProduct = item
      this.editMode = true
    },
    deleteProduct() {
      this.loading = true; 
      let params = {
        whitelabel_uuid: this.getWhiteLabel.uuid,
        id: this.selectedProduct.id
      }
      this.$apollo
        .mutate({
          mutation: PRODUCT_DELETE_MUTATION,
          variables: params
        })
        .then(data => {
          this.$store.commit('app/setNotifier', this.$t('globals.success'));
          this.$apollo.queries.moduleProducts.refetch()
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false;
        });
    },
    closeEditMode(val) {
      this.editMode = false
      if (val) {
        this.$apollo.queries.moduleProducts.refetch();  
      }
      this.selectedProduct = null
    },
    closeCheckoutLinkDialog() {
      this.checkoutLinkDialog = false;
      this.selectedProduct = null
    },
    reload() {
      if (this.selectedCategory) {
        this.params.category_id = this.selectedCategory.id 
      } else {
        delete(this.params.category_id)
      }
      if (this.search) {
        if (this.search.length < 2) {
          return
        }
        this.params.search = this.search 
      } else {
        delete(this.params.search)
      }
  
      this.$apollo.queries.moduleProducts.fetchMore({
        variables: this.params,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          return fetchMoreResult
        },
      })
    },
  },
  apollo: {
    moduleProducts: {
      fetchPolicy: "network-only",
      query() {
        return PRODUCTS_QUERY;
      },
      variables() {
        return this.params
      },
      skip() {
        return !this.params.whitelabel_uuid;
      },
    },
    moduleProductsCategories: {
      fetchPolicy: "network-only",
      query() {
        return PRODUCTS_CATEGORIES_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.getWhiteLabel.uuid
        }
      },
      skip() {
        return !this.getWhiteLabel;
      },
    }
  },
}
</script>
<style lang="scss" scoped>
 
</style>
